import React from 'react';
import { connect } from 'react-redux';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TablePagination from '@material-ui/core/TablePagination';
import TableRow from '@material-ui/core/TableRow';
import Paper from '@material-ui/core/Paper';
import Dialog from '@material-ui/core/Dialog';
import Grid from '@material-ui/core/Grid';
import TextField from '@material-ui/core/TextField';
import Tooltip from '@material-ui/core/Tooltip';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import FormControl from '@material-ui/core/FormControl';
import InputAdornment from '@material-ui/core/InputAdornment';
import SGEIcon from '@material-ui/icons/CompareArrows';
import SearchIcon from '@material-ui/icons/Search';
import CancelIcon from '@material-ui/icons/DeleteForever';
import IconButton from '@material-ui/core/IconButton';
import SalesForceAPI from '../../../API/SalesForceAPI';
import AdminSalesForceSyncTableHeader from './AdminSalesForceSyncTableHeader';
import LinearProgress from '../../Utils/LinearProgress';
import SnackbarAlert from '../../Utils/SnackbarAlert';
import ConfirmationDialog from '../../Utils/ConfirmationDialog';
import { STARTING_TABLE_PAGE, DEFAULT_ROWS_PER_PAGE, NO_TABLE_DATA_MESSAGE, ADMIN_GROUP } from '../../../Utils/Constants';
import { handleServerError, handleSpecialCharacters } from '../../../Utils/Helper';

const styles = theme => ({
  paperRoot: {
    width: '100%',
    backgroundColor: '#F6F6F6',
  },
  tableWrapper: {
    overflowX: 'auto',
  },
  tblBorder: {
    border: '2px solid #ffffff',
    fontSize: '12px'
  },
  jsoncol: {
    maxWidth: '550px',
    wordBreak: 'break-word',
  },
  textCenter: {
    textAlign: 'center',
  },
  tblRow: {
    backgroundColor: '#F2F2F2',
    '&:hover': {
      backgroundColor: theme.palette.primary.light
    },
    '&:nth-of-type(odd)': {
      backgroundColor: '#E1E1E1',
      '&:hover': {
        backgroundColor: theme.palette.primary.light
      },
    },
    height: '30px'
  },
  button: {
    margin: theme.spacing.unit
  },
  buttonSize: {
    width: '36px',
    height: '36px'
  },
  divToolbar: {
    paddingLeft: '12px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    height: '52px'
  },
  padRight: {
    paddingRight: '12px',
  },
  searchArea: {
    display: 'flex',
    alignItems: 'inherit'
  },
  formControl: {
    minWidth: '150px'
  },
  searchField: {
    width: '240px',
    margin: '16px 8px 0px 0px'
  },
  searchIconMargin: {
    marginRight: '4px'
  },
  tblFooter: {
    height: '30px',
    minHeight: '30px'
  },
  pageButton: {
    width: '30px',
    height: '30px',
    margin: '0px 8px'
  },
  activeWidth: {
    width: '88px'
  },
});


class AdminSalesForceSyncTable extends React.Component {
  _isMounted;
  searchTimer = null;
  salesForceAPI;

  constructor(props) {
    super(props);
    this.state = {
      data: [],
      page: STARTING_TABLE_PAGE,
      rowsPerPage: DEFAULT_ROWS_PER_PAGE,
      count: 0,
      searchHas: '',
      loading: false,
      reloading: false,
      openError: false,
      alertMessage: '',
      openDialog: false,
      confirmationQuestion: '',
      confirmationMessage: '',
      confirmationFunction: () => { },
      cancelFunction: () => { },
      availabilityStateId: 0,
    };
    this.salesForceAPI = new SalesForceAPI();
  }

  componentDidMount() {
    this._isMounted = true;
    this.reloadTable();
  };

  componentWillUnmount() {
    this._isMounted = false;
  }

  reloadTable() {
    this.setState({ reloading: true });
    this.salesForceAPI.getQueueList(STARTING_TABLE_PAGE, this.state.rowsPerPage, this.state.search).then((response) => {
      if (this._isMounted)
        this.setState({
          data: response.data.ResultList,
          count: response.data.NumberFilteredResults,
          page: STARTING_TABLE_PAGE,
          reloading: false
        });
    }).catch((error) => {
      if (this._isMounted)
        this.setState({
          reloading: false,
          openError: true,
          alertMessage: handleServerError(error)
        })
    });
  };

  reloadTableBySearch(searchBy) {
    this.setState({ reloading: true });
    clearTimeout(this.searchTimer);

    this.searchTimer = setTimeout(() => {
      this.salesForceAPI.getQueueList(STARTING_TABLE_PAGE, this.state.rowsPerPage, this.state.search).then((response) => {
        if (this._isMounted)
          this.setState({
            data: response.data.ResultList,
            count: response.data.NumberFilteredResults,
            search: searchBy,
            page: STARTING_TABLE_PAGE,
            reloading: false
          });
      }).catch((error) => {
        if (this._isMounted)
          this.setState({
            reloading: false,
            openError: true,
            alertMessage: handleServerError(error)
          })
      });
    }, 1500);
  };

  handleChangePage = (event, page) => {
    this.setState({ reloading: true });
    this.salesForceAPI.getQueueList(page, this.state.rowsPerPage, this.state.search).then((response) => {
      if (this._isMounted)
        this.setState({
          data: response.data.ResultList,
          count: response.data.NumberFilteredResults,
          page: page,
          reloading: false
        });
    }).catch((error) => {
      if (this._isMounted)
        this.setState({
          reloading: false,
          openError: true,
          alertMessage: handleServerError(error)
        })
    });
  };

  handleChangeRowsPerPage = event => {
    this.setState({ reloading: true });
    this.salesForceAPI.getQueueList(this.state.page, event.target.value, this.state.search).then((response) => {
      if (this._isMounted)
        this.setState({
          data: response.data.ResultList,
          count: response.data.NumberFilteredResults,
          rowsPerPage: event.target.value,
          reloading: false
        });
    }).catch((error) => {
      if (this._isMounted)
        this.setState({
          reloading: false,
          openError: true,
          alertMessage: handleServerError(error)
        })
    });
  };

  handleSubmitSalesForceSync = () => {
    this.setState({ loading: true });
    this.salesForceAPI.getSyncQueue().then((success) => {
      this.setState({
        loading: false,
        success: true
      });
      setTimeout(() => {
        this.setState({
          success: false
        });
        this.handleCloseDialog();
      }, 500)
    }).catch((error) => {
      this.setState({
        loading: false,
        success: false,
      })
      this.handleCloseDialog();
    });
  };

  handleClickSalesForceSync = () => {
    this.setState({
      openDialog: true,
      confirmationQuestion: 'Sincronizar informação com SalesForce?',
      confirmationMessage: 'Pretende sincronizar toda a informação de stocks com o sales force?',
      confirmationFunction: this.handleSubmitSalesForceSync,
      cancelFunction: this.handleCloseDialog
    });
  }

  handleSearch = event => {
    this.setState({ search: handleSpecialCharacters(event.target.value) });
    this.reloadTableBySearch(handleSpecialCharacters(event.target.value));
  };

  handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    this.setState({
      openError: false,
      alertMessage: ''
    });
  };

  handleCloseDialog = () => {
    this.setState({
      openDialog: false,
      reloading: false,
      success: false
    });
  };

  handleClickCancel = (queueId) => {
    this.setState({
      openDialog: true,
      confirmationQuestion: 'Cancelar envio SalesForce?',
      confirmationMessage: 'Pretende cancelar todas as sincronizações deste par hashcode\\Num. Referência?',
      confirmationFunction: () => this.handleSubmitCancelSync(queueId),
      cancelFunction: this.handleCloseDialog
    });
  };

  handleSubmitCancelSync = (queueId) => {
    this.setState({ loading: true });
    this.salesForceAPI.cancelSyncQueue(queueId).then((success) => {
      this.setState({
        loading: false,
        success: true
      });
      setTimeout(() => {
        this.setState({
          success: false
        });
        this.handleCloseDialog();
        this.reloadTable();
      }, 500)
    }).catch((error) => {
      this.setState({
        loading: false,
        success: false,
      })
      this.handleCloseDialog();
      this.reloadTable();
    });
  };

  renderTableRecords(componentDataList) {
    const { classes } = this.props;

    const componentData = componentDataList.map(componentItem => {
      return (
        <TableRow key={componentItem.ID} tabIndex={-1} className={classes.tblRow}>
          <TableCell className={classNames(classes.tblBorder, classes.textCenter)} component='th' scope='row' padding='none'>
            {componentItem.MethodName}
          </TableCell>
          <TableCell className={classNames(classes.tblBorder, classes.textCenter)} component='th' scope='row' padding='none'>
            {componentItem.HashCode}
          </TableCell>
          <TableCell className={classNames(classes.tblBorder, classes.textCenter)} component='th' scope='row' padding='none'>
            {componentItem.QueueStatus}
          </TableCell>
          <TableCell className={classNames(classes.tblBorder, classes.textCenter)} component='th' scope='row' padding='none'>
            {componentItem.ReferenceQuoteId}
          </TableCell>
          <TableCell className={classNames(classes.tblBorder, classes.textCenter)} component='th' scope='row' padding='none'>
            {componentItem.RequestDate}
          </TableCell>
          <TableCell className={classNames(classes.tblBorder, classes.jsoncol)} component='th' scope='row' padding='none'>
            {componentItem.JsonToSend}
          </TableCell>
          <TableCell className={classNames(classes.tblBorder, classes.textCenter, classes.iconsWidth)} padding='none'>
            {componentItem.QueueStatus.indexOf("ERROR") < 0 ? null :
              <Tooltip title='Cancelar Sincronização' placement='bottom' >
                <IconButton aria-label='Cancel' onClick={() => { this.handleClickCancel(componentItem.ID) }} className={classes.buttonSizeTbl} >
                  <CancelIcon />
                </IconButton>
              </Tooltip>
            }
          </TableCell>
        </TableRow>
      );
    })
    return componentData;
  }

  renderNoRecordMessages() {
    return NO_TABLE_DATA_MESSAGE;
  }

  renderToolbar() {
    const { classes } = this.props;
    return (
      <div className={classNames(classes.divToolbar, this.props.user.UserRoleType.indexOf(ADMIN_GROUP) < 0 ? classes.padRight : '')}>

        <Typography variant='title' id='tableTitle'>
          {this.props.titleTableList}
        </Typography>

        <div className={classes.searchArea}>
          <FormControl className={classes.formControl}>
            <Grid container spacing={8} alignItems='flex-end'>
              <Grid item>
                <TextField
                  id='searchBy'
                  label=''
                  placeholder='procurar'
                  type='search'
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position='start' className={classes.searchIconMargin}>
                        <SearchIcon />
                      </InputAdornment>
                    )
                  }}
                  className={classes.searchField}
                  value={this.state.search}
                  onChange={this.handleSearch}
                />
              </Grid>
            </Grid>
          </FormControl>
          <Tooltip title='SalesForce Sync' placement='bottom' >
            <Button variant='fab' color='primary' className={classNames(classes.button, classes.buttonSize)} onClick={() => { this.handleClickSalesForceSync() }}>
              <SGEIcon />
            </Button>
          </Tooltip>
        </div>
      </div >
    );
  };

  render() {
    const { classes } = this.props;
    const { data, rowsPerPage, page, count, reloading, loading, success } = this.state;

    return (
      <Paper className={classes.paperRoot}>

        {this.renderToolbar()}

        <div className={classes.tableWrapper}>
          <Table>
            <AdminSalesForceSyncTableHeader />
            <TableBody>
              {this.renderTableRecords(data)}
            </TableBody>
          </Table>
          {(!data.length) ? this.renderNoRecordMessages() : null}
        </div>
        {reloading && <LinearProgress />}
        <TablePagination
          component='div'
          count={count}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50]}
          labelRowsPerPage={'Resultados por página:'}
          page={page}
          backIconButtonProps={{ className: classes.pageButton }}
          nextIconButtonProps={{ className: classes.pageButton }}
          onChangePage={this.handleChangePage}
          onChangeRowsPerPage={this.handleChangeRowsPerPage}
          labelDisplayedRows={({ from, to, count }) => `A mostrar resultados ${from}-${to} de ${count}`}
          classes={{ toolbar: classes.tblFooter }}
        />

        <Dialog
          open={this.state.openDialog}
          onClose={this.handleCloseDialog}
        >
          <ConfirmationDialog
            openConfirmationDialog={this.state.openDialog}
            handleClose={this.handleCloseDialog.bind(this)}
            confirmationQuestion={this.state.confirmationQuestion}
            confirmationMessage={this.state.confirmationMessage}
            confirmationFunction={this.state.confirmationFunction.bind(this)}
            cancelFunction={this.state.cancelFunction.bind(this)}
            loading={loading}
            success={success}
          />
        </Dialog>

        <SnackbarAlert openError={this.state.openError} alertMessage={this.state.alertMessage} handleCloseSnackbar={this.handleCloseSnackbar} />

      </Paper>
    );
  }
}

AdminSalesForceSyncTable.propTypes = {
  classes: PropTypes.object.isRequired
};

const mapStateToProps = state => ({
  ...state,
});

export default connect(mapStateToProps)(withStyles(styles)(AdminSalesForceSyncTable));


// WEBPACK FOOTER //
// ./src/components/Administration/AdminSalesForceSync/AdminSalesForceSyncTable.js